import * as Y from 'yjs';
import * as awarenessProtocol from 'y-protocols/awareness';

export interface UserInfo {
  id: string;
  name: string;
  color: string;
}

export interface PreviewState {
  props: Record<string, unknown>;
  version: number;
  timestamp: number;
}

export interface AwarenessState {
  user: UserInfo;
  selection: string[];
  previews: Record<string, PreviewState>;
}

export class OptimisticPreviewManager {
  private localPreviews = new Map<string, Record<string, unknown>>();
  private electricVersions = new Map<string, number>();
  // Commented out - may be needed for batched commits
  // private commitQueue = new Map<string, Record<string, any>>();
  // private commitTimer: NodeJS.Timeout | null = null;
  // private readonly commitDelay = 100; // ms
  // Track last applied preview per shape to avoid out-of-order jitter
  private lastAppliedPreview = new Map<string, { props: Record<string, unknown>; timestamp: number }>();

  constructor(
    _ydoc: Y.Doc, // May be needed for future Y.Doc operations
    private awareness: awarenessProtocol.Awareness
  ) {
    // Initialize awareness state if it's null
    if (this.awareness.getLocalState() === null) {
      this.awareness.setLocalState({});
    }
  }

  updatePreview(shapeId: string, changes: Record<string, unknown>): void {
    const current = this.localPreviews.get(shapeId) || {};
    const updated = { ...current, ...changes };
    
    // Update local state immediately
    this.localPreviews.set(shapeId, updated);
    
    // Broadcast via awareness (generic)
    const currentState = this.awareness.getLocalState() as AwarenessState | null;
    const previews = currentState?.previews || {};
    
    const newPreview = {
      props: updated,
      version: (previews[shapeId]?.version || 0) + 1,
      timestamp: Date.now()
    };

    // Use setLocalStateField to preserve existing user info and only update previews
    this.awareness.setLocalStateField('previews', {
      ...previews,
      [shapeId]: newPreview
    });
    
    // Do NOT commit preview changes to Y.Doc during drag.
    // Final commit happens via the regular store->Electric path on drag end.
  }

  // Commented out - may be needed for batched commits in the future
  // private scheduleCommit(shapeId: string, changes: Record<string, any>): void {
  //   this.commitQueue.set(shapeId, changes);
  //   
  //   if (this.commitTimer) {
  //     clearTimeout(this.commitTimer);
  //   }
  //   
  //   this.commitTimer = setTimeout(() => {
  //     this.flushCommits();
  //   }, this.commitDelay);
  // }

  // private flushCommits(): void {
  //   if (this.commitQueue.size === 0) return;
  //   
  //   this.ydoc.transact(() => {
  //     const shapes = this.ydoc.getMap('shapes');
  //     
  //     for (const [shapeId, changes] of this.commitQueue) {
  //       const shape = shapes.get(shapeId);
  //       if (shape) {
  //         shapes.set(shapeId, { ...shape, ...changes });
  //       }
  //     }
  //   });
  //   
  //   this.commitQueue.clear();
  // }

  onElectricUpdate(shapeId: string): void {
    // Drop the preview - authoritative data wins
    this.localPreviews.delete(shapeId);
    
    // Clear from awareness
    const currentState = this.awareness.getLocalState() as AwarenessState;
    const previews: Record<string, PreviewState> = (currentState?.previews || {}) as Record<string, PreviewState>;
    delete previews[shapeId];
    this.awareness.setLocalStateField('previews', previews);
    
    // Track version to detect changes
    this.electricVersions.set(shapeId, Date.now());
    // Clear any last-applied preview overlay once Electric lands
    this.lastAppliedPreview.delete(shapeId);
  }
  
  clearPreviewForShape(shapeId: string): void {
    // Clear local preview when drag ends
    this.localPreviews.delete(shapeId);
    
    // Clear from awareness to notify others - use setLocalStateField to preserve user info
    const currentState = this.awareness.getLocalState() as AwarenessState;
    const previews = { ...(currentState?.previews || {}) };
    delete previews[shapeId];
    this.awareness.setLocalStateField('previews', previews);
    
    // Clear last applied preview
    this.lastAppliedPreview.delete(shapeId);
  }

  getShapeState<T extends Record<string, unknown>>(shapeId: string, baseState: T): T {
    // Get local user ID to exclude their own previews
    // const localState = this.awareness.getLocalState();
    // const localUserId = localState?.user?.id; // TODO: Remove if not needed
    
    // Check all awareness states for previews EXCEPT the local user
    const allStates = Array.from(this.awareness.getStates().entries()) as [number, AwarenessState][];
    
    // Debug logs disabled
    if (shapeId === 'shape-aa4e0577-0dc7-4b15-b81a-89aa862b943f') {
      // no-op
    }
    
    // Find the most recent preview for this shape from OTHER users
    const previews = allStates
      .filter(([clientId]) => clientId !== this.awareness.clientID)
      .map(([, state]) => ({
        userId: state?.user?.id,
        clientId: (state as unknown as { clientId?: number })?.clientId,
        preview: state?.previews?.[shapeId]
      }))
      .filter(p => p.preview)
      .sort((a, b) => (b.preview?.timestamp || 0) - (a.preview?.timestamp || 0));
    
    if (previews.length > 0 && previews[0].preview) {
      const incoming = previews[0].preview as PreviewState;
      const last = this.lastAppliedPreview.get(shapeId);
      if (!last || (incoming.timestamp || 0) >= (last.timestamp || 0)) {
        this.lastAppliedPreview.set(shapeId, { props: incoming.props, timestamp: incoming.timestamp });
        return { ...baseState, ...(incoming.props as Partial<T>) } as T;
      }
      // Older frame arrived out of order; retain previously applied preview props
      if (last) {
        return { ...baseState, ...(last.props as Partial<T>) } as T;
      }
    } else {
      // No active preview frame: continue to overlay the last-applied preview
      // to avoid snapback between frames. This will be cleared on Electric commit.
      const last = this.lastAppliedPreview.get(shapeId);
      if (last) {
        return { ...baseState, ...(last.props as Partial<T>) } as T;
      }
    }
    
    return baseState;
  }

  clearLocalPreviews(): void {
    this.localPreviews.clear();
    // Commented out - related to batched commits
    // this.commitQueue.clear();
    // 
    // if (this.commitTimer) {
    //   clearTimeout(this.commitTimer);
    //   this.commitTimer = null;
    // }
    
    // Clear all previews from awareness - use setLocalStateField to preserve user info
    this.awareness.setLocalStateField('previews', {});
  }

  forceCommit(): void {
    // Commented out - related to batched commits
    // if (this.commitTimer) {
    //   clearTimeout(this.commitTimer);
    //   this.commitTimer = null;
    // }
    // 
    // this.flushCommits();
  }

  setUserSelection(shapeIds: string[]): void {
    // Use setLocalStateField to preserve user info and only update selection
    this.awareness.setLocalStateField('selection', shapeIds);
  }

  getUserSelections(): Map<string, { user: UserInfo; selection: string[] }> {
    const selections = new Map<string, { user: UserInfo; selection: string[] }>();
    const allStates = Array.from(this.awareness.getStates().entries()) as [number, AwarenessState][];
    
    for (const [clientId, state] of allStates) {
      if (state?.user && state?.selection?.length > 0) {
        selections.set(clientId.toString(), {
          user: state.user,
          selection: state.selection
        });
      }
    }
    
    return selections;
  }

  destroy(): void {
    this.clearLocalPreviews();
  }
}